import { memo } from 'react';
import { View } from 'react-native';
import Svg, { G, Line, Path, Text } from 'react-native-svg';
import { CategoryTooltip } from '../ComboChart/CategoryTooltip';
import { fitText, textWidth } from '../ComboChart/labels';
import { horizontalBarPath } from '../ComboChart/paths';
import { valueScale } from '../ComboChart/scale';
import { ComboBarStyle, ComboTheme, ComboValueAxis } from '../ComboChart/types';

export interface HorizontalBarsSeries {
  key: string;
  /** As the tooltip names it. */
  name?: string;
  color: string;
  values: (number | undefined)[];
  labels?: (string | undefined)[];
}

export interface HorizontalBarsChartProps {
  width: number;
  categories: string[];
  series: HorizontalBarsSeries[];
  /** The category names down the left. */
  categoryAxis: { show: boolean; title?: string };
  /** The values along the bottom. */
  valueAxis: ComboValueAxis;
  gridLines: boolean;
  dataLabels: boolean;
  bar: ComboBarStyle;
  theme: ComboTheme;
}

const kBarThickness = 12;
const kBarGap = 2;

/** The height a chart of these rows takes. */
export function horizontalBarsHeight(
  props: Pick<
    HorizontalBarsChartProps,
    'categories' | 'series' | 'valueAxis' | 'bar' | 'theme'
  >,
): number {
  const f = props.theme.fontSize;
  const k = Math.max(props.series.length, 1);
  const band = (k * kBarThickness + (k - 1) * kBarGap) / (1 - props.bar.gap);
  const axis = props.valueAxis.show
    ? f + 8 + (props.valueAxis.title ? f + 6 : 0)
    : 4;
  return Math.ceil(band * props.categories.length + axis + 4);
}

/**
 * Bars running left to right, one band per category, its name on the left
 * (cut to a share of the width; hovering or tapping a row shows it in full
 * with its values): what a ranking of long names needs.
 */
export const HorizontalBarsChart = memo((props: HorizontalBarsChartProps) => {
  const { width, categories, series, bar, theme, valueAxis } = props;
  const f = theme.fontSize;
  const height = horizontalBarsHeight(props);
  const k = Math.max(series.length, 1);
  const thickness = k * kBarThickness + (k - 1) * kBarGap;
  const band = thickness / (1 - bar.gap);
  const names = props.categoryAxis.show
    ? categories.map(name => fitText(name, width * 0.3, f))
    : [];
  const left =
    (names.length
      ? Math.max(...names.map(name => textWidth(name, f))) + 8
      : 2) + (props.categoryAxis.show && props.categoryAxis.title ? f + 6 : 0);
  const values = series.flatMap(entry =>
    entry.values.filter((value): value is number => value !== undefined),
  );
  const labelWidth = props.dataLabels
    ? Math.max(
        0,
        ...series.flatMap(entry =>
          (entry.labels ?? []).map(text => (text ? textWidth(text, f) : 0)),
        ),
      ) + 6
    : 0;
  const negative = values.some(value => value < 0);
  // Labels sit past each bar's end: on the left of a negative one.
  const plotLeft = left + (negative ? labelWidth : 0);
  const plotHeight = band * categories.length;
  const probe = valueScale(values, 100, {
    min: valueAxis.min,
    max: valueAxis.max,
  });
  // The last tick is centred on the plot's right end: half of it must fit.
  const lastTick = valueAxis.show
    ? textWidth(valueAxis.format(probe.ticks[probe.ticks.length - 1] ?? 0), f)
    : 0;
  const right = Math.max(labelWidth + 8, lastTick / 2 + 2);
  const plotWidth = Math.max(width - plotLeft - right, 40);
  const scale = valueScale(values, plotWidth, {
    min: valueAxis.min,
    max: valueAxis.max,
  });
  const toX = (value: number) => plotLeft + scale.toY(value);
  const zero = toX(Math.min(Math.max(0, scale.min), scale.max));
  const top = 2;
  const rowsOf = (i: number) =>
    series.flatMap(entry => {
      const value = entry.values[i];
      if (value === undefined) return [];
      return [
        {
          color: entry.color,
          name: entry.name,
          value: entry.labels?.[i] ?? valueAxis.format(value),
        },
      ];
    });
  return (
    <View style={{ width, height }}>
      <Svg width={width} height={height}>
        {props.gridLines
          ? scale.ticks.map(tick => (
              <Line
                key={tick}
                x1={toX(tick)}
                x2={toX(tick)}
                y1={top}
                y2={top + plotHeight}
                stroke={theme.grid}
                strokeDasharray="3,3"
              />
            ))
          : null}
        <Line
          x1={zero}
          x2={zero}
          y1={top}
          y2={top + plotHeight}
          stroke={theme.axisLine}
        />
        {categories.map((_, i) => {
          const y = top + band * i + (band - thickness) / 2;
          return (
            <G key={i}>
              {names.length ? (
                <Text
                  x={left - 6}
                  y={top + band * (i + 0.5) + f * 0.35}
                  fontSize={f}
                  fontFamily={theme.fontFamily}
                  fill={theme.axisText}
                  textAnchor="end"
                >
                  {names[i]}
                </Text>
              ) : null}
              {series.map((entry, s) => {
                const value = entry.values[i];
                if (value === undefined) return null;
                const by = y + s * (kBarThickness + kBarGap);
                const end = toX(value);
                const text = entry.labels?.[i];
                return (
                  <G key={entry.key}>
                    <Path
                      d={horizontalBarPath(
                        zero,
                        end,
                        by,
                        kBarThickness,
                        bar.cornerRadius,
                      )}
                      fill={entry.color}
                    />
                    {props.dataLabels && text ? (
                      <Text
                        x={value < 0 ? end - 4 : end + 4}
                        y={by + kBarThickness / 2 + f * 0.35}
                        fontSize={f}
                        fontFamily={theme.fontFamily}
                        fill={theme.label}
                        textAnchor={value < 0 ? 'end' : 'start'}
                      >
                        {text}
                      </Text>
                    ) : null}
                  </G>
                );
              })}
            </G>
          );
        })}
        {valueAxis.show
          ? scale.ticks.map(tick => (
              <Text
                key={tick}
                x={toX(tick)}
                y={top + plotHeight + f + 4}
                fontSize={f}
                fontFamily={theme.fontFamily}
                fill={theme.axisText}
                textAnchor="middle"
              >
                {valueAxis.format(tick)}
              </Text>
            ))
          : null}
        {valueAxis.show && valueAxis.title ? (
          <Text
            x={plotLeft + plotWidth / 2}
            y={height - 3}
            fontSize={f}
            fontFamily={theme.fontFamily}
            fill={theme.axisTitle}
            textAnchor="middle"
          >
            {valueAxis.title}
          </Text>
        ) : null}
        {props.categoryAxis.show && props.categoryAxis.title ? (
          <Text
            x={f * 0.8}
            y={top + plotHeight / 2}
            fontSize={f}
            fontFamily={theme.fontFamily}
            fill={theme.axisTitle}
            textAnchor="middle"
            transform={`rotate(-90 ${f * 0.8} ${top + plotHeight / 2})`}
          >
            {props.categoryAxis.title}
          </Text>
        ) : null}
      </Svg>
      <CategoryTooltip
        width={width}
        height={height}
        bands={categories.map((_, i) => ({
          x: 0,
          y: top + band * i,
          width,
          height: band,
        }))}
        names={categories}
        rowsOf={rowsOf}
        theme={theme}
      />
    </View>
  );
});
